import { useState } from "react";
import { makeStyles, withStyles, Theme } from "@material-ui/core/styles";
import { observer } from "mobx-react-lite";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";

import { StateService } from "../services/StateService";
import lockIcon from "../assets/lock-icon.svg";
import "./PriceGraph.scss";

const useStyles = makeStyles(() => ({
  title: {
    padding: "18px 25px",
  },
  price: {
    fontSize: "40px",
    fontWeight: 500,
    marginBottom: "18px",
    fontFamily: "Gilroy-Medium, sans-serif",
    color: "#282828",
  },
  acceptedBox: {
    backgroundColor: "#DCF2E2",
    fontWeight: 500,
    marginBottom: "12px",
    fontFamily: "Gilroy-Medium, sans-serif",
    fontSize: "16px",
    color: "#0F842E",
    paddingBottom: "3px",
    paddingTop: "3px",
    borderRadius: "15px",
  },
  dateBox: {
    fontSize: "16px",
    fontWeight: 500,
    fontFamily: "Gilroy-Medium, sans-serif",
  },
}));

interface PriceGraphProps {
  state: StateService;
}

const TooltipTitle = (props: any) => {
  const classes = useStyles();
  return (
    <div className={classes.title}>
      <div className={classes.price}>{props?.price?.toFixed(0)}FET</div>
      <div className={classes.dateBox}>on {props.date}</div>
    </div>
  );
};
//removed: <div className={classes.acceptedBox}>Accepted</div>

const GraphTooltip = withStyles((theme: Theme) => ({
  popper: {
    margin: "10px 0",
  },
  tooltip: {
    backgroundColor: "white",
    color: "rgba(0, 0, 0, 0.87)",
    fontSize: theme.typography.pxToRem(12),
    borderRadius: 20,
    textAlign: "center",
  },
  arrow: {
    fontSize: 40,
    color: "white",
  },
}))(Tooltip);

const CustomizedDot = (props: any) => {
  const [open, setOpen] = useState<boolean>(false);

  let colour = props.colourOverride ? props.colourOverride : "#8884d8";

  return (
    <GraphTooltip
      arrow
      title={
        <TooltipTitle
          price={props?.payload?.price}
          date={props?.payload?.fullDate}
        />
      }
      placement="top"
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
    >
      <circle
        cx={props?.cx}
        cy={props?.cy}
        r={open ? 9 : 5}
        stroke={open ? "#D34AFF" : colour}
        fill={open ? "#fff" : "#1D1F23"}
        strokeWidth={open ? 7.5 : 2.5}
      />
    </GraphTooltip>
  );
};

const WalletNotAvailable = {
  overlayClass: "overlayBox",
  messageDisplay: "block",
};
const WalletAvailable = { overlayClass: "", messageDisplay: "none" };

export const PriceGraph = observer((props: PriceGraphProps) => {
  const walletStatus = props.state.web3.getSyncedContract() ? WalletAvailable : WalletNotAvailable;

  // Prices are split into three groups, before now in time, after now in time, and some points that can be used to join
  // the two (and have a colour gradient/fade).
  let pricesBeforeNow = props.state.priceStore.getFormatedPrices(true);
  let pricesAfterNow = props.state.priceStore.getFormatedPrices(false);
  let pricesJoining = pricesBeforeNow.length >= 1 && pricesAfterNow.length >= 1 ? [pricesBeforeNow[pricesBeforeNow.length-1], pricesAfterNow[0]] : [];

  return props.state.priceStore.isEmpty() ? (
    <p style={{ color: "white", fontSize: "26px" }}>Auction initialising...</p>
  ) : (
    <div className="overlayOuterBox">
      <div className={walletStatus.overlayClass}>
        <div style={{ height: "400px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              width={500}
              height={400}
              margin={{
                top: 10,
                right: 30,
                left: 0,
                bottom: 0,
              }}
            >

            <defs>
              <linearGradient id="colorUv" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#8884d8" />
                <stop offset="100%" stopColor="#808080" />
              </linearGradient>
            </defs>

              <CartesianGrid vertical={false} stroke="#838CA1" />
              <XAxis
                dataKey="time"
                type="category"
                allowDuplicatedCategory={false}
                tick={{ fontSize: 18 }}
                stroke="#fff"
                style={{ fontFamily: "Gilroy-Regular" }}
                axisLine={true}
                tickLine={false}
                dy={5}
              />
              <YAxis
                unit=""
                type="number"
                domain={[props.state.priceStore.getPriceRange()[0], 'auto']}
                tick={{ fontSize: 18 }}
                stroke="#fff"
                style={{ fontFamily: "Gilroy-Regular" }}
                axisLine={false}
                tickLine={false}
                tickCount={10}
                yAxisId="priceAxis"
                padding={{ top: 0, bottom: 0 }}
                width={70}
                dx={-20}
              />
              <Line
                type="monotone"
                dataKey="price"
                isAnimationActive={false}
                data={pricesBeforeNow}
                stroke="#8884d8"
                strokeWidth={2.5}
                fill="#1D1F23"
                yAxisId="priceAxis"
                dot={<CustomizedDot /> } />

              <Line
                type="monotone"
                dataKey="price"
                isAnimationActive={false}
                data={pricesJoining}
                stroke="url(#colorUv)"
                strokeWidth={2.5}
                fill="#1D1F23"
                yAxisId="priceAxis"
                dot={<CustomizedDot  /> } />

              <Line
                type="monotone"
                dataKey="price"
                isAnimationActive={false}
                data={pricesAfterNow}
                stroke="#808080"
                strokeWidth={2.5}
                fill="#808080"
                yAxisId="priceAxis"
                dot={<CustomizedDot colourOverride="#808080" /> } />

            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div
        className="overlayContent"
        style={{ display: walletStatus.messageDisplay }}
      >
        <img src={lockIcon} alt="icon" />
        <Typography gutterBottom variant="h5" component="h4">
          Connect wallet to see actual prices
        </Typography>
      </div>
    </div>
  );
});
